const Pikaday = require('pikaday');
import {
  dateFormat,
  months,
  weekdays,
  weekdaysShort,
  firstDayOfWeek,
  dateFormatUnicode,
  minDateFormatUnicodeLength,
} from './DateI18n';
import parse from 'date-fns/parse';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import addDays from 'date-fns/addDays';
import startOfWeek from 'date-fns/startOfWeek';
import isSaturday from 'date-fns/isSaturday';
import max from 'date-fns/max';
import { take } from 'rxjs/operators';

class DateRangeSelector {
  /*
  elements = {
    start_container: HTML element,
    start_field: HTML element,
    end_container: HTML element,
    end_field: HTML element
  }

  availableDatesRanges = [{
    startDate: Date object,
    endDate: Date object
  }]
  */

  constructor(
    elements,
    languageCode,
    searchModel,
    availableDatesRanges,
    options = {
      shortestRangeInDays: 1,
      longestRangeInDays: 90,
    },
  ) {
    this.elements = elements;
    this.languageCode = languageCode;
    this.searchModel = searchModel;
    this.availableDatesRanges = availableDatesRanges;
    this.options = options;
    this.selectedStartDate = null;
    this.selectedEndDate = null;
    this.startDateSelector = null;
    this.endDateSelector = null;
    this.isInitializing = true;
    this.init();
  }

  isStartDateInAvailableDatesRanges(date) {
    if (this.availableDatesRanges.length === 0) {
      return true;
    }
    for (const range of this.availableDatesRanges) {
      if (this.isStartDateInAvailableDatesRange(range, date)) {
        return true;
      }
    }
    return false;
  }

  isStartDateInAvailableDatesRange(range, date) {
    return (
      range.startDate === null ||
      (range.startDate <= date &&
        date <=
          addDays(
            range.endDate,
            -1 * this.options.shortestRangeInDays,
          ))
    );
  }

  isEndDateInAvailableDatesRanges(date) {
    if (this.availableDatesRanges.length === 0) {
      return true;
    }
    for (const range of this.availableDatesRanges) {
      if (this.isEndDateInAvailableDatesRange(range, date)) {
        return true;
      }
    }
    return false;
  }

  isEndDateInAvailableDatesRange(range, date) {
    return (
      range.endDate === null ||
      (range.endDate >= date &&
        date >=
          addDays(
            range.startDate,
            this.options.shortestRangeInDays,
          ))
    );
  }

  isDateRangeValidSelection(startDate, endDate) {
    return (
      startDate !== null &&
      endDate !== null &&
      this.isStartDateInAvailableDatesRanges(startDate) &&
      this.isEndDateInAvailableDatesRanges(endDate) &&
      this.isDateValidStartDateRangeSelection(startDate, endDate) &&
      this.isDateValidEndDateRangeSelection(endDate, startDate)
    );
  }

  /**
   *Detects if date is in range for start input field.
   *
   * @param {Date} date
   * @returns {boolean}
   * @memberof DateRangeSelector
   */
  isDateValidStartDateRangeSelection(date, endDate = this.selectedEndDate) {
    if (endDate === null) {
      return true;
    }
    return (
      date < endDate &&
      Math.abs(differenceInCalendarDays(date, endDate)) <=
        this.options.longestRangeInDays
    );
  }

  /**
   *Detects if date is in range for end input field.
   *
   * @param {Date} date
   * @returns {boolean}
   * @memberof DateRangeSelector
   */
  isDateValidEndDateRangeSelection(date, startDate = this.selectedStartDate) {
    if (startDate === null) {
      return true;
    }
    return (
      date > startDate &&
      Math.abs(differenceInCalendarDays(date, startDate)) <= this.options.longestRangeInDays
    );
  }

  showStartDaySelector() {
    this.elements.start_container.classList.add('datepicker-visible');
  }

  hideStartDaySelector() {
    this.elements.start_container.classList.remove('datepicker-visible');
  }

  showEndDaySelector() {
    this.elements.end_container.classList.add('datepicker-visible');
  }

  hideEndDaySelector() {
    this.elements.end_container.classList.remove('datepicker-visible');
  }

  setSelectedStartDate(startDate) {
    if (startDate !== null && this.isStartDateInAvailableDatesRanges(startDate)) {
      this.selectedStartDate = startDate;
      if (this.selectedStartDate > this.selectedEndDate || Math.abs(differenceInCalendarDays(this.selectedStartDate, this.selectedEndDate)) > this.options.longestRangeInDays) {
        this.selectedEndDate = addDays(this.selectedStartDate, this.options.shortestRangeInDays);
        this.searchModel.setArrivalDepartureRange(
          this.selectedStartDate,
          this.selectedEndDate,
        );
      } else {
        this.searchModel.setArrivalDate(this.selectedStartDate);
      }
      return true;
    }
    return false;
  }

  setSelectedStartDateInPikaday(startDate) {
    if (startDate !== null && this.isStartDateInAvailableDatesRanges(startDate)) {
      this.startDateSelector.setDate(startDate);
      if (this.selectedEndDate !== null && startDate > this.selectedEndDate) {
        const dayAfter = addDays(startDate, 1);
        this.endDateSelector.setDate(dayAfter);
      }
    }
  }

  setSelectedEndDate(endDate) {
    if (
      endDate !== null &&
      this.isEndDateInAvailableDatesRanges(endDate) &&
      this.isDateValidEndDateRangeSelection(endDate, this.selectedStartDate)
    ) {
      this.selectedEndDate = endDate;
      this.searchModel.setDepartureDate(endDate);
      return true;
    }
    return false;
  }

  setSelectedEndDateInPikaday(endDate) {
    if (
      endDate !== null &&
      this.isEndDateInAvailableDatesRanges(endDate) &&
      this.isDateValidEndDateRangeSelection(endDate, this.selectedStartDate)
    ) {
      this.endDateSelector.setDate(endDate);
    }
  }

  unselectEndDateInPikaday() {
    this.endDateSelector.setDate(null);
    this.unselectRangeInPikaday();
  }

  unselectStartDateInPikaday() {
    this.startDateSelector.setDate(null);
    this.unselectRangeInPikaday();
  }

  unselectRangeInPikaday() {
    this.startDateSelector.setStartRange(null);
    this.endDateSelector.setStartRange(null);
    this.startDateSelector.setEndRange(null);
    this.endDateSelector.setEndRange(null);
  }

  displaySelectedRangeInPikaday(startDate, endDate) {
    if (this.isDateRangeValidSelection(startDate, endDate)) {
      this.startDateSelector.setStartRange(startDate);
      this.endDateSelector.setStartRange(startDate);
      this.startDateSelector.setEndRange(endDate);
      this.endDateSelector.setEndRange(endDate);
    } else {
      this.unselectRangeInPikaday();
    }
  }

  setSelectedRange(startDate, endDate) {
    if (this.isDateRangeValidSelection(startDate, endDate)) {
      this.selectedStartDate = startDate;
      this.selectedEndDate = endDate;
      this.searchModel.setArrivalDepartureRange(startDate, endDate);
      return true;
    }
    return false;
  }

  getCommonPikadaySettings() {
    const numberOfMonths = Modernizr.mq('(max-width: 768px)') ? 1 : 2;
    return {
      numberOfMonths,
      format: dateFormat(this.languageCode),
      firstDay: firstDayOfWeek(this.languageCode),
      bound: true,
      yearRange: 2,
      defaultDate: new Date(),
      minDate: new Date(),
      i18n: {
        months: months(this.languageCode),
        weekdays: weekdays(this.languageCode),
        weekdaysShort: weekdaysShort(this.languageCode),
        previousMonth: 'Previous Month',
        nextMonth: 'Next Month',
      },
      keyboardInput: false,
    };
  }

  initPikaday() {
    const obj = this;
    const startPikadaySettings = {
      ...this.getCommonPikadaySettings(),
      field: this.elements.start_field,
      container: this.elements.start_container,
      onSelect: date => {
        obj.setSelectedStartDate(date);
        obj.endDateSelector.setDate(obj.selectedEndDate);
        obj.displaySelectedRangeInPikaday(date, obj.selectedEndDate);
        if (!this.isInitializing) {
          obj.endDateSelector.show();
        } else {
          obj.isInitializing = false;
        }
      },
      onOpen: () => {
        obj.showStartDaySelector();
      },
      onClose: () => {
        obj.hideStartDaySelector();
      },
      disableDayFn: date => {
        return !obj.isStartDateInAvailableDatesRanges(date);
      },
    };

    this.startDateSelector = new Pikaday(startPikadaySettings);

    const endPikadaySettings = {
      ...this.getCommonPikadaySettings(),
      field: this.elements.end_field,
      trigger: this.elements.end_field,
      container: this.elements.end_container,
      onSelect: date => {
        obj.setSelectedEndDate(date);
        obj.displaySelectedRangeInPikaday(obj.selectedStartDate, date);
      },
      onOpen: () => {
        obj.showEndDaySelector();
      },
      onClose: () => {
        obj.hideEndDaySelector();
      },
      disableDayFn: date => {
        return (
          !obj.isDateValidEndDateRangeSelection(date) ||
          !obj.isEndDateInAvailableDatesRanges(date)
        );
      },
    };
    this.endDateSelector = new Pikaday(endPikadaySettings);
  }

  isLegalDateKey(key) {
    return '0123456789.-'.indexOf(key) !== -1;
  }

  initStartDateFieldInput() {
    this.elements.start_field.addEventListener('keyup', eventObj => {
      if (
        !this.isLegalDateKey(eventObj.key) ||
        this.elements.start_field.value.length <
          minDateFormatUnicodeLength(this.languageCode)
      ) {
        return false;
      }

      let parsedDate = parse(
        this.elements.start_field.value,
        dateFormatUnicode(this.languageCode),
        new Date(),
        { awareOfUnicodeTokens: true },
      );

      if (parsedDate === null) {
        return false;
      }
      this.elements.start_field.blur();
      this.elements.start_field.click();
    });
  }

  initEndDateFieldInput() {
    this.elements.end_field.addEventListener('keyup', eventObj => {
      if (
        !this.isLegalDateKey(eventObj.key) ||
        this.elements.end_field.value.length <
          minDateFormatUnicodeLength(this.languageCode)
      ) {
        return false;
      }

      let parsedDate = parse(
        this.elements.end_field.value,
        dateFormatUnicode(this.languageCode),
        new Date(),
        { awareOfUnicodeTokens: true },
      );

      if (parsedDate === null) {
        return false;
      }
      this.elements.end_field.blur();
      this.elements.end_field.click();
    });
  }

  initTouchDevice() {
    //TODO: refactor
    [...document.querySelectorAll('.search-date-input')].map(input => {
      input.setAttribute('readonly', true);
      input.addEventListener('click', event => {
        event.preventDefault();
      });
    });

    [...document.querySelectorAll('.prevent-touch')].map(element => {
      element.addEventListener('click', event => {
        event.preventDefault();
        if (event.target.matches('.prevent-touch-start')) {
          if (this.elements.start_container.matches('.datepicker-visible')) {
            this.startDateSelector.hide();
          } else {
            window.setTimeout(() => {
              this.startDateSelector.show();
            }, 0);
          }
        }
        if (event.target.matches('.prevent-touch-end')) {
          if (this.elements.end_container.matches('.datepicker-visible')) {
            this.endDateSelector.hide();
          } else {
            window.setTimeout(() => {
              this.endDateSelector.show();
            }, 0);
          }
        }
      });
    });
  }

  getDefaultSelectedRangeInAvailibleRange(range) {
    let firstAvailableSaturday, firstAvailableDate, currentDate, today, todayDate;
    today = new Date();
    todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    currentDate = isSaturday(todayDate) ? addDays(todayDate, 7) : todayDate;
    if (range.startDate === null) {
      firstAvailableDate = currentDate;
    } else {
      firstAvailableDate = max([currentDate, range.startDate]);
    }
    if (isSaturday(firstAvailableDate)) {
      firstAvailableSaturday = firstAvailableDate;
    } else {
      firstAvailableSaturday = addDays(startOfWeek(firstAvailableDate, {weekStartsOn: 6}), 7);
    }
    const defaultSelectedRange = {
      startDate: firstAvailableSaturday,
      endDate: addDays(firstAvailableSaturday, 7),
    };
    if (this.isDateRangeValidSelection(defaultSelectedRange.startDate, defaultSelectedRange.endDate)) {
      return defaultSelectedRange;
    }
    return null;
  }

  getDefaultSelectedRangeInAvailibleRanges() {
    for (const range of this.availableDatesRanges) {
      const defaultSelectedRange = this.getDefaultSelectedRangeInAvailibleRange(range);
      if (defaultSelectedRange !== null) {
        return defaultSelectedRange;
      }
    }
    return null;
  }

  initData() {
    this.searchModel.availableDatesRange$.subscribe(availableDatesRanges => {
      this.availableDatesRanges = availableDatesRanges;
      if (
        !this.isStartDateInAvailableDatesRanges(this.selectedStartDate) ||
        !this.isEndDateInAvailableDatesRanges(this.selectedEndDate)
      ) {
        this.isInitializing = true;
        const defaultSelectedRange = this.getDefaultSelectedRangeInAvailibleRanges();
        if (defaultSelectedRange !== null) {
          this.setSelectedRange(defaultSelectedRange['startDate'], defaultSelectedRange['endDate']);
          this.setSelectedStartDateInPikaday(defaultSelectedRange['startDate']);
          this.setSelectedEndDateInPikaday(defaultSelectedRange['endDate']);
          this.displaySelectedRangeInPikaday(defaultSelectedRange['startDate'], defaultSelectedRange['endDate']);
        }
      }
    });
    this.searchModel.searchData$.pipe(take(1)).subscribe(state => {
      if ('arrival' in state && 'departure' in state) {
        this.setSelectedRange(state['arrival'], state['departure']);
        this.displaySelectedRangeInPikaday(state['arrival'], state['departure']);
      }
      if ('arrival' in state) {
        this.selectedStartDate = state['arrival'];
      }
      if ('departure' in state) {
        this.selectedEndDate = state['departure'];
      }
      if ('arrival' in state) {
        this.setSelectedStartDateInPikaday(state['arrival']);
      }
      if ('departure' in state) {
        this.setSelectedEndDateInPikaday(state['departure']);
      }
    });
  }

  init() {
    this.initPikaday();
    this.initData();
    if (!Modernizr.touchevents) {
      this.initStartDateFieldInput();
      this.initEndDateFieldInput();
    } else {
      this.initTouchDevice();
    }
  }
}

export default DateRangeSelector;



// WEBPACK FOOTER //
// ./src/js/search/DateRangeSelector.js